// Auth Types
export type AuthResponseDto = {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
};

export type LoginRequest = {
  username: string;
  password: string;
};

export type RefreshRequestDto = {
  refreshToken: string;
};

// User Types
export enum UserRole {
  User = 0,
  Moderator = 1,
  Admin = 2,
  SuperAdmin = 3,
}

export enum Permission {
  // User management (numeric values match backend Permission.cs)
  ViewUsers = 100,
  CreateUsers = 101,
  UpdateUsers = 102,
  DeleteUsers = 103,
  ManageUserRoles = 104,

  // Foam domain
  ViewFoamReferential = 200,
  ManageFoamReferential = 201,
  ViewProductionRuns = 202,
  ManageProductionRuns = 203,
  ManageEquipment = 204,

  // System administration
  ViewSystemStats = 300,
  ViewSystemLogs = 301,
  ManageSystemSettings = 302,
  ViewAnalytics = 303,
  ManageDatabase = 304,

  // Security
  ManageSecurity = 400,
  ViewAuditLogs = 401,
}

export type UserDto = {
  id: number;
  username: string;
  role: UserRole;
  roleDisplayName: string;
  roleDescription: string;
  permissions: Permission[];
  createdAtUtc: string;
  isLocked: boolean;
  lastLoginAtUtc?: string;
  passwordUpdatedAtUtc?: string;
  failedLoginAttempts: number;
};

export type UserCreateDto = {
  username: string;
  password: string;
  role: UserRole;
};

export type UserUpdateDto = {
  username: string;
  role: UserRole;
};

export type ChangePasswordDto = {
  currentPassword: string;
  newPassword: string;
};

export type ChangeRoleDto = {
  newRole: UserRole;
};

// Session Types
export type SessionInfoDto = {
  sessionId: string;
  deviceFingerprint: string;
  userAgent: string;
  ipAddress: string;
  location?: string;
  createdAtUtc: string;
  lastAccessedAtUtc: string;
  isCurrentSession: boolean;
  isCurrent?: boolean;
  deviceType?: string;
  browser?: string;
  username?: string;
};

// Admin Types
export type RoleInfo = {
  value: UserRole;
  name: string;
  displayName: string;
  description: string;
  permissions: Permission[];
};

export type PermissionInfo = {
  value: Permission;
  name: string;
  category: string;
};

export type SystemStatistics = {
  totalUsers: number;
  usersByRole: Record<string, number>;
  activeUsers: number;
  lockedUsers: number;
};

// Audit Types
export type AuditLogDto = {
  id: number;
  eventType: string;
  eventCategory: string;
  severity: string;
  userId?: number;
  username?: string;
  targetUserId?: number;
  targetUsername?: string;
  action: string;
  details?: string;
  ipAddress?: string;
  userAgent?: string;
  sessionId?: string;
  success: boolean;
  errorMessage?: string;
  createdAtUtc: string;
};

export type AuditLogQueryDto = {
  userId?: number;
  eventType?: string;
  eventCategory?: string;
  severity?: string;
  fromDate?: string;
  toDate?: string;
  pageNumber?: number;
  pageSize?: number;
};

export type AuditLogPagedResponseDto = {
  logs: AuditLogDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
};

export type AuditStatisticsDto = {
  totalEvents: number;
  eventsByType: Record<string, number>;
  eventsByCategory: Record<string, number>;
  eventsBySeverity: Record<string, number>;
  failedEvents: number;
  successfulEvents: number;
};
